import {Option} from '../option';
import {SortTableRow, SortTableUniqueRowId} from './types';
import {ButtonConfig} from '../button';
import {PillColor, PillFill, PillLetter, PillText} from '../pill';
import {User, UserId} from '../user';
import {Role} from '../fga/role';
import { ObjectModel } from '../object.model';
import {ColorOption} from '../appearance';
import {TileId} from '../tile';
import {FacetValue, Tag} from '../tag';
import {AlarmEvent} from '../alarm';

// Constants for special cell values
export const EMPTY_CELL_DISPLAY = '(empty)';

export abstract class SortTableCell {
  abstract readonly cellType:
    'TextPill' |
    'TitleDescription' |
    'TitleSharingInfo' |
    'UserLink' |
    'StatusDot' |
    'KebabMenu' |
    'Empty' |
    'Icon' |
    'Toggle' |
    'Buttons' |
    'Swatch' |
    'DisplayName' |
    'RolePicker' |
    'CurrentValue' |
    'Tags';

  customSortValue: string;

  clickCallback: () => any;
  onInitCallback: () => any;
  copyable?: boolean;


  /**
   * If the title value is not the one on which the sort function
   * should base its sorting, use this function to set the value
   * on which sorting should be based.
   *
   * @param value
   * @see sortingFnCustomSortValue
   */
  addSortValue(value: string): this {
    this.customSortValue = value;
    return this;
  }


  /**
   * A callback for anytime a user clicks on a SortTableCell
   * extended class component.
   *
   * @param fn
   */
  addClickCallback(fn: () => any): this {
    this.clickCallback = fn;
    return this;
  }


  /**
   * A callback for anytime a SortTableCell extended class
   * component is initialized.
   *
   * @param fn
   */
  addOnInitCallback(fn: () => any): this {
    this.onInitCallback = fn;
    return this;
  }


  /**
   * This is for use in conjunction with addSortValue
   *
   * @param columnIndex
   * @param rows
   * @see addSortValue
   */
  static sortingFnCustomSortValue(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const titleA = (a.cells[columnIndex] as SortTableCell).customSortValue.toLowerCase();
      const titleB = (b.cells[columnIndex] as SortTableCell).customSortValue.toLowerCase();
      if (titleA < titleB) {
        return -1;
      }

      if (titleB < titleA) {
        return 1;
      }

      return 0;
    });
  }
}


/**
 * Configures a cell that shows the identify of a given user.
 */
export class SortTableCellUserLink extends SortTableCell {
  readonly cellType = 'UserLink';

  userId: UserId;
  user: User;


  constructor(obj: {
    userId: UserId,
    user?: User,
  }) {
    super();
    this.userId = obj.userId;
    this.user = obj.user;
  }


  static sortingFnUsername(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const textA = (a.cells[columnIndex] as SortTableCellUserLink).user?.Name.toLowerCase();
      const textB = (b.cells[columnIndex] as SortTableCellUserLink).user?.Name.toLowerCase();
      if (textA < textB) {
        return -1;
      }

      if (textB < textA) {
        return 1;
      }

      return 0;
    });
  }
}


/**
 * Configures a cell that has both a title and a subtitle. The subtitle
 * is enclosed in a pill.
 */
export class SortTableCellTextPill extends SortTableCell {
  readonly cellType = 'TextPill';

  mainText: string;
  pillText: PillText;
  monospaceText: boolean;
  monospacePill: boolean;

  mainTextIcon: string;
  mainTextColor: PillColor;
  pillLetter: PillLetter;
  pillColor: PillColor;
  pillFill: PillFill;

  showPillFirst: boolean = false;
  displayName?: string;
  facetValues?: string[];


  constructor(
    mainText: string,
    pillText: PillText,
    monospaceText: boolean = false,
    monospacePill: boolean = false,
  ) {
    super();

    this.mainText = mainText;
    this.pillText = pillText;
    this.monospaceText = monospaceText;
    this.monospacePill = monospacePill;
  }


  setMainTextColor(color: PillColor): this {
    this.mainTextColor = color;
    return this;
  }


  setMainTextIcon(icon: string): this {
    this.mainTextIcon = icon;
    return this;
  }


  setPillColor(color: PillColor): this {
    this.pillColor = color;
    return this;
  }


  setPillFill(fill: PillFill): this {
    this.pillFill = fill;
    return this;
  }


  setPillLetter(icon: PillLetter): this {
    this.pillLetter = icon;
    return this;
  }


  setShowPillFirst(setting: boolean): this {
    this.showPillFirst = setting;
    return this;
  }


  static sortingFnMainText(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const textA = (a.cells[columnIndex] as SortTableCellTextPill).mainText.toLowerCase();
      const textB = (b.cells[columnIndex] as SortTableCellTextPill).mainText.toLowerCase();
      if (textA < textB) {
        return -1;
      }

      if (textB < textA) {
        return 1;
      }

      return 0;
    });
  }


  static sortingFnPillText(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const pillA = (a.cells[columnIndex] as SortTableCellTextPill).pillText;
      const pillB = (b.cells[columnIndex] as SortTableCellTextPill).pillText;
      if (pillA < pillB) {
        return -1;
      }

      if (pillB < pillA) {
        return 1;
      }

      return 0;
    });
  }
}

/**
 * Configures a cell specifically designed for being the title cell for
 * subjects. The cell shows the object name and relevant sharing/ownership
 * information relative to the user who is logged in.
 */
export class SortTableCellTitleSharingInfo extends SortTableCell {
  readonly cellType = 'TitleSharingInfo';

  title: string;
  object: ObjectModel;
  displayName?: string;
  facetTags: FacetValue[] = [];
  alarmEvent: AlarmEvent | null = null;

  constructor(obj: {
    title: string,
    object: ObjectModel,
    displayName?: string,
  }) {
    super();
    this.title = obj.title;
    this.object = obj.object;
    this.displayName = obj.displayName;
  }

  static sortingFnTitle(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const titleA = (a.cells[columnIndex] as SortTableCellTitleSharingInfo).title.toLowerCase();
      const titleB = (b.cells[columnIndex] as SortTableCellTitleSharingInfo).title.toLowerCase();
      if (titleA < titleB) {
        return -1;
      }

      if (titleB < titleA) {
        return 1;
      }

      return 0;
    });
  }
}

/**
 * Configures a cell that has displayName of the tile
 */
export class SortTableCellDisplayName extends SortTableCell {
  readonly cellType = 'DisplayName';

  displayName: string;


  constructor(private providedDisplayName: string) {
    super();

    this.displayName = providedDisplayName || '';
  }


  static sortingFnDisplayName(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const titleA = (a.cells[columnIndex] as SortTableCellDisplayName).displayName.toLowerCase();
      const titleB = (b.cells[columnIndex] as SortTableCellDisplayName).displayName.toLowerCase();
      if (titleA < titleB) {
        return -1;
      }

      if (titleB < titleA) {
        return 1;
      }

      return 0;
    });
  }
}

/**
 * Configures a cell that has a toggle in it.
 */
export class SortTableCellToggle extends SortTableCell {
  readonly cellType = 'Toggle';

  toggled: boolean = false;
  callback: (toggled: boolean, checkboxEl?: HTMLInputElement) => any;


  constructor(
    toggled: boolean,
    callback?: (toggled: boolean, checkboxEl?: HTMLInputElement) => any,
  ) {
    super();

    this.toggled = toggled;
    this.callback = callback;
  }
}


/**
 * Configures a cell that has both a title and a description.
 */
export class SortTableCellTitleDescription extends SortTableCell {
  readonly cellType = 'TitleDescription';

  title: string;
  description: string;

  descriptionIsPill: boolean = false;
  descriptionPillLetter: PillLetter = '';
  
  copyable: boolean = false;


  constructor(
    title: string,
    description: string,
    descriptionIsPill: boolean = false,
    descriptionPillLetter: PillLetter = '',
  ) {
    super();

    this.title = title;
    this.description = description;
    this.descriptionIsPill = descriptionIsPill;
    this.descriptionPillLetter = descriptionPillLetter;
  }
  
  /**
   * Makes this cell copyable with click-to-copy functionality
   * @param handler An object with a copyToClipboard method
   * @returns this for method chaining
   */
  makeCopyable(handler?: { copyToClipboard: (value: string) => any }): this {
    // Only make copyable if there's meaningful content
    const hasContent = this.title && this.title !== EMPTY_CELL_DISPLAY;
    
    if (hasContent) {
      this.copyable = true;
      if (handler) {
        this.addClickCallback(() => handler.copyToClipboard(this.title));
      }
    }
    
    return this;
  }


  static sortingFnTitle(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const titleA = (a.cells[columnIndex] as SortTableCellTitleDescription).title.toLowerCase();
      const titleB = (b.cells[columnIndex] as SortTableCellTitleDescription).title.toLowerCase();
      if (titleA < titleB) {
        return -1;
      }

      if (titleB < titleA) {
        return 1;
      }

      return 0;
    });
  }
}

export type SortTableStatusDotColor = 'green' | 'yellow' | 'red' | 'gray';

/**
 * Configures a cell with a status dot and status string.
 */
export class SortTableCellStatusDot extends SortTableCell {
  readonly cellType = 'StatusDot';

  color: SortTableStatusDotColor;
  label: string;


  constructor(
    color: SortTableStatusDotColor,
    label: string,
  ) {
    super();

    this.color = color;
    this.label = label;
  }


  static sortingFnStatusLabel(columnIndex: number, rows: SortTableRow[]): SortTableRow[] {
    return rows.sort((a: SortTableRow, b: SortTableRow): number => {
      const titleA = (a.cells[columnIndex] as SortTableCellStatusDot).label.toLowerCase();
      const titleB = (b.cells[columnIndex] as SortTableCellStatusDot).label.toLowerCase();
      if (titleA < titleB) {
        return -1;
      }

      if (titleB < titleA) {
        return 1;
      }

      return 0;
    });
  }
}

/**
 * Configures a cell with a kebab menu.
 */
export class SortTableCellKebabMenu extends SortTableCell {
  readonly cellType = 'KebabMenu';

  isLoading: boolean = false;
  options: Option[];

  /**
   *
   * @param uniqueId - Any arbitrary ID that will be unique to this kebab menu compared
   *    to any others on the screen
   * @param initialOptions
   * @param menuTitle
   * @param bulkSelectEnabled
   * @param maxExpectedOptions - Maximum number of options expected (for calculating viewport margin before options load)
   */
  constructor(
    public uniqueId: SortTableUniqueRowId,
    initialOptions: Option[],
    public menuTitle: string = '',
    public bulkSelectEnabled: boolean = false,
    public maxExpectedOptions: number = 5,
  ) {
    super();
    this.options = initialOptions;
  }
}

/**
 * Configures simply an empty cell.
 */
export class SortTableCellEmpty extends SortTableCell {
  readonly cellType = 'Empty';
}

export type SortTableCellIconColor = 'violet' | 'indigo' | 'green' | 'red' | 'yellow' | 'gray';

/**
 * Configures a cell with an icon.
 */
export class SortTableCellIcon extends SortTableCell {
  readonly cellType = 'Icon';

  icon: string;
  color: SortTableCellIconColor;
  tooltip?: string;


  constructor(icon: string, color?: SortTableCellIconColor, tooltip?: string) {
    super();

    this.icon = icon;
    this.color = color;
    this.tooltip = tooltip;
  }
}

/**
 * Configures a cell with one or more buttons.
 */
export class SortTableCellButtons extends SortTableCell {
  readonly cellType = 'Buttons';

  buttons: ButtonConfig[];


  constructor(buttons: ButtonConfig[]) {
    super();

    this.buttons = buttons;
  }
}

/**
 * Configures a cell with one or more buttons.
 */
export class SortTableRolePicker extends SortTableCell {
  readonly cellType = 'RolePicker';

  userId: UserId;
  object: ObjectModel;
  preselectedRoles: Role[];


  constructor(userId: UserId, object: ObjectModel, roles: Role[]) {
    super();
    this.userId = userId;
    this.object = object;
    this.preselectedRoles = roles;
  }
}

/**
 * SortTableCellSwatch
 *
 * A cell for displaying a color swatch
 */
export class SortTableCellSwatch extends SortTableCell {
  readonly cellType = 'Swatch';

  color: ColorOption;
  size: number;

  constructor(color: ColorOption, size: number = 28) {
    super();
    this.color = color;
    this.size = size;
  }
}

/**
 * Configures a cell that displays the current value of a tile with history icon
 */
export class SortTableCellCurrentValue extends SortTableCell {
  readonly cellType = 'CurrentValue';

  tileId: TileId;
  hasValue: boolean = false;

  constructor(tileId: TileId) {
    super();
    this.tileId = tileId;
  }
}

/**
 * Configures a cell that displays applied tags for an object, with collapse/expand.
 */
export class SortTableCellTags extends SortTableCell {
  readonly cellType = 'Tags';

  private _tags: Tag[] | null = null;
  get tags(): Tag[] | null { return this._tags; }
  set tags(value: Tag[] | null) {
    this._tags = value ? [...value].sort((a, b) => a.Label.localeCompare(b.Label)) : null;
  }

  loaded: boolean = false;
  collapsed: boolean = true;

  constructor(public tileId: TileId) {
    super();
  }
}
